/* OEST × Norda-style interactions layered on Dawn.
   - Line-by-line heading reveal (Norda "splitText")
   - Hover-to-open mega menu on desktop
   - Product gallery arrows (desktop horizontal gallery)
   - Size drawer + sticky product bar */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Split headings into lines and reveal on scroll ---------- */
  var SPLIT_SELECTOR = [
    '.oest-banner--statement .banner__heading',
    '.collection__title .title',
    '.title-wrapper-with-link .title',
    '.collection-hero__title',
    '.newsletter__wrapper h2',
    '.product__title h1'
  ].join(',');

  function splitLines(el) {
    if (el.dataset.oestSplit) return;
    // Keep screen-reader-only prefixes (e.g. Dawn's "Collection:") out of the visible lines
    var hidden = el.querySelector('.visually-hidden');
    var hiddenHTML = hidden ? hidden.outerHTML : '';
    var clone = el.cloneNode(true);
    clone.querySelectorAll('.visually-hidden').forEach(function (h) { h.remove(); });
    var text = clone.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;
    el.dataset.oestSplit = '1';
    if (!hidden) el.setAttribute('aria-label', text);
    var words = text.split(' ');
    el.innerHTML = words.map(function (w) { return '<span class="oest-w" aria-hidden="true">' + w.replace(/</g, '&lt;') + '</span>'; }).join(' ');
    var spans = el.querySelectorAll('.oest-w');
    var lines = [];
    var top = null;
    spans.forEach(function (s) {
      var t = s.offsetTop;
      if (top === null || Math.abs(t - top) > 2) { lines.push([]); top = t; }
      lines[lines.length - 1].push(s.textContent);
    });
    el.innerHTML = hiddenHTML + lines.map(function (l, i) {
      return '<span class="oest-line" aria-hidden="true"><span style="--i:' + i + '">' + l.join(' ') + '</span></span>';
    }).join('');
    el.classList.add('oest-split');
  }

  function initSplit() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    var els = document.querySelectorAll(SPLIT_SELECTOR);
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    els.forEach(function (el) {
      // Skip headings Dawn renders as rich text with links/markup we'd destroy
      if (el.querySelector('a, img, svg')) return;
      splitLines(el);
      io.observe(el);
    });
  }

  /* ---------- Desktop mega menu opens on hover ---------- */
  function initHoverMenu() {
    var mq = window.matchMedia('(min-width: 990px) and (hover: hover)');
    document.querySelectorAll('header-menu details.mega-menu, header-menu details').forEach(function (details) {
      var li = details.closest('li') || details;
      var closeTimer;
      li.addEventListener('mouseenter', function () {
        if (!mq.matches) return;
        clearTimeout(closeTimer);
        document.querySelectorAll('header-menu details[open]').forEach(function (d) { if (d !== details) d.removeAttribute('open'); });
        if (!details.hasAttribute('open')) {
          details.setAttribute('open', '');
          var summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'true');
        }
      });
      li.addEventListener('mouseleave', function () {
        if (!mq.matches) return;
        closeTimer = setTimeout(function () {
          details.removeAttribute('open');
          var summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'false');
        }, 180);
      });
    });
  }

  /* ---------- Product gallery arrows (desktop) ---------- */
  function initGallery() {
    var list = document.querySelector('.product__media-wrapper .product__media-list');
    var wrapper = document.querySelector('.product__media-wrapper');
    if (!list || !wrapper || list.children.length < 2) return;
    var nav = document.createElement('div');
    nav.className = 'oest-gallery-nav';
    nav.innerHTML =
      '<button type="button" aria-label="Previous image" data-dir="-1"><svg viewBox="0 0 14 10" width="14" height="10" aria-hidden="true"><path d="M13 5H1M5 1L1 5l4 4" stroke="currentColor" stroke-width="1.3" fill="none"/></svg></button>' +
      '<button type="button" aria-label="Next image" data-dir="1"><svg viewBox="0 0 14 10" width="14" height="10" aria-hidden="true"><path d="M1 5h12M9 1l4 4-4 4" stroke="currentColor" stroke-width="1.3" fill="none"/></svg></button>';
    wrapper.style.position = 'relative';
    wrapper.appendChild(nav);
    var prev = nav.children[0];
    var next = nav.children[1];
    function update() {
      prev.disabled = list.scrollLeft < 5;
      next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 5;
    }
    nav.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b) return;
      var item = list.children[0];
      list.scrollBy({ left: Number(b.dataset.dir) * (item ? item.getBoundingClientRect().width : list.clientWidth), behavior: 'smooth' });
    });
    list.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------- Size drawer ---------- */
  function initDrawer() {
    var drawer = document.querySelector('.oest-size-drawer');
    if (!drawer) return;
    // The drawer stays inside <product-info> so Dawn keeps updating the variant picker and buy buttons.
    var lastTrigger = null;
    var triggers = document.querySelectorAll('[data-oest-drawer-open]');

    function open(trigger) {
      lastTrigger = trigger || null;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('oest-drawer-open');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      var panel = drawer.querySelector('.oest-size-drawer__panel');
      setTimeout(function () { panel && panel.focus(); }, 50);
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('oest-drawer-open');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (lastTrigger) lastTrigger.focus();
    }
    document.addEventListener('click', function (e) {
      var o = e.target.closest('[data-oest-drawer-open]');
      if (o) { e.preventDefault(); open(o); return; }
      if (e.target.closest('[data-oest-drawer-close]')) { e.preventDefault(); close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
    // Close the size drawer when the product is added so Dawn's cart drawer can take over
    drawer.addEventListener('submit', function () { setTimeout(close, 150); }, true);
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () { if (drawer.classList.contains('is-open')) close(); });
    }
  }

  /* ---------- Sticky product bar ---------- */
  function initStickyBar() {
    var bar = document.querySelector('[data-oest-sticky]');
    var anchor = document.querySelector('.product__info-container .oest-size-trigger');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var btn = bar.querySelector('button');
    new IntersectionObserver(function (entries) {
      var e = entries[0];
      var show = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (btn) btn.tabIndex = show ? 0 : -1;
    }).observe(anchor);
  }

  /* ---------- Scroll-linked header (Satisfy-style) ----------
     The header is sticky; scrolling down slides it up by exactly the distance scrolled, scrolling up slides it
     back by exactly the distance scrolled. Exposes --oest-header-visible (px of header currently on screen). */
  function initScrollHeader() {
    var header = document.querySelector('.section-header');
    if (!header) return;
    var root = document.documentElement;
    var isIndex = document.body.classList.contains('template-index');
    var offset = 0;          // px of the header hidden above the viewport
    var lastY = window.scrollY;
    var ticking = false;
    header.classList.add('oest-scroll-header');

    function naturalTop() {
      // document position where the header sits in flow (below the announcement bar); measured without
      // our transform and with sticky disabled so it reflects the in-flow position
      var prevT = header.style.transform;
      header.style.transform = '';
      header.style.setProperty('position', 'relative', 'important');
      var top = header.getBoundingClientRect().top + window.scrollY;
      header.style.removeProperty('position');
      header.style.transform = prevT;
      return Math.max(0, top);
    }
    var start = naturalTop();
    function menuOpen() {
      return !!header.querySelector('details[open]') || root.classList.contains('oest-drawer-open');
    }
    function apply() {
      ticking = false;
      var h = header.offsetHeight;
      root.style.setProperty('--oest-header-h', h + 'px');
      var y = Math.max(0, window.scrollY - start);
      var d = y - lastY;
      lastY = y;
      if (y <= 0) offset = 0;
      else offset = Math.min(h, Math.max(0, offset + d));
      if (menuOpen()) offset = 0;
      header.style.transform = offset ? 'translateY(' + (-offset) + 'px)' : '';
      // Visible header height measured from the viewport top (0 when hidden or scrolled past naturally)
      var visible = Math.max(0, header.getBoundingClientRect().bottom);
      root.style.setProperty('--oest-header-visible', Math.round(visible) + 'px');
      if (isIndex) header.classList.toggle('oest-header--solid', y > 0 && offset < h);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(apply); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { start = naturalTop(); onScroll(); });
    lastY = Math.max(0, window.scrollY - start);
    apply();
  }

  /* ---------- Mouse wheel vs trackpad (carousel arrows only for mouse users) ----------
     Browsers don't say which device scrolled, but a notched mouse wheel looks different from a trackpad:
     Firefox reports wheels in lines; Chrome/Safari give the legacy wheelDeltaY as exactly -3 × deltaY for
     trackpads but not for wheel notches (±120 steps; macOS mice give deltaY 4.0002…); trackpads also move
     sideways. The latest verdict wins and is remembered for the next page. */
  function initWheelKind() {
    var root = document.documentElement;
    var KEY = 'oest-wheel-kind';
    try { if (localStorage.getItem(KEY) === 'mouse') root.classList.add('oest-wheel-mouse'); } catch (e) {}
    function kindOf(e) {
      if (e.ctrlKey || e.shiftKey) return null;          // pinch-zoom / shift+wheel sideways: not telling
      if (e.deltaMode === 1 || e.deltaMode === 2) return 'mouse';       // Firefox wheel: lines/pages
      if (e.deltaX !== 0) return 'trackpad';                            // two-finger swipes drift sideways
      var dy = Math.abs(e.deltaY), w = Math.abs(e.wheelDeltaY || 0);
      if (!dy) return null;
      if (w && w % 120 === 0) return 'mouse';                           // Windows/Linux wheel notches
      var frac = dy % 1;
      if (frac > 0 && frac < 0.01) return 'mouse';                      // macOS wheel: 4.000244…, 8.000488…
      if (w && Math.abs(w - 3 * dy) <= 1.5) return 'trackpad';           // legacy delta = 3 × pixels (rounded)
      return w ? 'mouse' : 'trackpad';
    }
    var last = null;
    window.addEventListener('wheel', function (e) {
      var kind = kindOf(e);
      if (!kind || kind === last) return;
      last = kind;
      root.classList.toggle('oest-wheel-mouse', kind === 'mouse');
      try { localStorage.setItem(KEY, kind); } catch (err) {}
    }, { passive: true });
  }

  function init() {
    initWheelKind();
    initScrollHeader();
    initSplit();
    initHoverMenu();
    initGallery();
    initDrawer();
    initStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Theme editor: re-run when sections reload
  document.addEventListener('shopify:section:load', function () {
    initSplit();
    initHoverMenu();
  });
})();
